"use client";

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import Link from "next/link";
import { memo } from "react";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ArtistPhoto } from "@/components/shared/photos/artist-photo";
import { usePrefetchOnHover } from "@/hooks/shared/use-smart-prefetch";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { trackArtistDiscovery } from "@/lib/services/analytics";
import { cn } from "@/lib/utils";

// Simple artist interface matching the database structure
interface SimpleArtist {
	id: string;
	name: string;
	slug?: string;
	image_url: string | null;
	spotify_popularity: number | null;
	spotify_genres: string[] | null;
	manual_genres: string[] | null;
	aliases?: string[];
	is_collaborative_alias?: boolean;
	is_group_alias?: boolean;
	group_slug?: string;
	primary_artist_name?: string;
	display_name?: string;
	collaborator_names?: string[];
	collaborative_data?: {
		primary_artist: {
			id: string;
			name: string;
			image_url: string | null;
			spotify_genres: string[] | null;
			manual_genres: string[] | null;
		};
		collaborators: Array<{
			id: string;
			name: string;
			image_url: string | null;
			spotify_genres: string[] | null;
			manual_genres: string[] | null;
		}>;
	};
}

interface ArtistCardProps {
	artist: SimpleArtist;
	priority?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	loading?: "eager" | "lazy";
	className?: string;
}

export const ArtistDiscoveryCard = memo<ArtistCardProps>(
	function ArtistDiscoveryCard({
		artist,
		priority = false,
		fetchPriority = "auto",
		loading = "lazy",
		className,
	}) {
		// Smart prefetching for better UX
		const { createPrefetchHandlers } = usePrefetchOnHover();
		const prefetchHandlers = createPrefetchHandlers("artist", artist.name);

		const displayName = artist.display_name || artist.name;

		const handleClick = () => {
			// Track artist discovery when card is clicked
			trackArtistDiscovery(artist.id, displayName, "discover", "viewed");
		};

		// Determine the correct href based on whether it's a group alias or regular artist
		const href =
			artist.is_group_alias && artist.group_slug
				? `/groups/${artist.group_slug}`
				: `/artists/${createArtistSlug(artist.primary_artist_name || artist.name, artist.slug)}`;

		return (
			<Link
				href={href}
				className={cn("group block", className)}
				style={{ WebkitTapHighlightColor: "transparent" }}
				onClick={handleClick}
				{...prefetchHandlers}
			>
				<Card
					className={cn(
						"flex h-full flex-col overflow-hidden",
						"hover:bg-muted/30 dark:hover:bg-muted/10",
						"active:bg-muted/50 dark:active:bg-muted/20",
						"active:scale-[0.99]",
						"transition-colors duration-100",
						"border dark:hover:border-border/60",
						"touch-manipulation",
					)}
				>
					<div className="relative aspect-square w-full overflow-hidden">
						<ArtistPhoto
							name={displayName}
							imageUrl={artist.image_url}
							size="xl"
							priority={priority}
							fetchPriority={fetchPriority}
							loading={loading}
							rounded="card"
							aspectRatio="square"
							className="h-full w-full rounded-none transition-transform duration-100 group-hover:scale-105"
						/>

						{/* Hover overlay */}
						<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
					</div>
					<CardHeader className="p-4 pb-2">
						<CardTitle className="transition-colors group-hover:text-primary">
							{displayName}
						</CardTitle>
						{artist.collaborator_names && artist.collaborator_names.length > 0 && (
							<p className="mt-1 text-muted-foreground text-xs">
								{artist.collaborator_names.join(" & ")}
							</p>
						)}
					</CardHeader>
					<CardContent className="flex flex-grow flex-col px-4 pb-4">
						<div className="min-h-[20px]">
							<GenreBadgeListFromData
								spotifyGenres={artist.spotify_genres || null}
								manualGenres={artist.manual_genres || null}
								maxDisplay={3}
							/>
						</div>
					</CardContent>
				</Card>
			</Link>
		);
	},
);

// Re-export the SimpleArtist type for use in other components
export type { SimpleArtist };
